import { useAxiosRequest } from "api/axiosRequest"
import { BASE_URL } from "api/url_helper"
import axios from "axios"
import Breadcrumbs from "components/Common/Breadcrumbs"
import React, { useCallback, useRef, useState } from "react"
import { Container } from "react-bootstrap"
import { Helmet } from "react-helmet-async"
import { toast } from "react-toastify"
import { Upload, FileText, CheckCircle, X, MapPin, Globe, Ship } from "lucide-react"

interface UploadSeaMarginRequest {
  file: string
  endPoint: string
  originalFileName: string
}

interface UploadSeaMarginResponse {
  filepath: string
}

type EndpointKey = "port" | "zone" | "voyage"

function SeaMarginWrapper() {
  const request = useAxiosRequest()
  const [fileName, setFileName] = useState<string>("")
  const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false)
  const [endPoint, setEndPoint] = useState<EndpointKey | "">("")
  const [selectedFiles, setSelectedFiles] = useState<{
    [key in EndpointKey]?: File
  }>({})
  const [uploadedFiles, setUploadedFiles] = useState<{
    [key in EndpointKey]?: string
  }>({})
  const [dragOver, setDragOver] = useState<EndpointKey | null>(null)

  const inputRefs = {
    "port": useRef<HTMLInputElement | null>(null),
    "zone": useRef<HTMLInputElement | null>(null),
    "voyage": useRef<HTMLInputElement | null>(null),
  }

  const uploadCategories: Array<{
    id: EndpointKey
    title: string
    description: string
    color: string
    icon: any
  }> = [
    {
      id: "port",
      title: "Port",
      description: "Upload port data CSV file",
      color: "primary",
      icon: MapPin,
    },
    {
      id: "zone",
      title: "Zone",
      description: "Upload zone data CSV file",
      color: "success",
      icon: Globe,
    },
    {
      id: "voyage",
      title: "Voyage",
      description: "Upload voyage data CSV file",
      color: "info",
      icon: Ship,
    },
  ]

  const handleFileSelect = useCallback((endpoint: EndpointKey, file: File) => {
    if (file.type !== "text/csv") {
      toast.warn("Please select a CSV file.", {
        position: "bottom-right",
        autoClose: 5000,
      })
      return
    }
    setSelectedFiles((prev) => ({ ...prev, [endpoint]: file }))
  }, [])

  const handleClick = useCallback((endpoint: EndpointKey) => {
    inputRefs[endpoint].current?.click()
  }, [])

  const handleInputChange = useCallback(
    (endpoint: EndpointKey, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ""
      if (file) {
        handleFileSelect(endpoint, file)
      }
    },
    [handleFileSelect]
  )

  const handleDrop = useCallback(
    (endpoint: EndpointKey, e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setDragOver(null)
      const file = e.dataTransfer.files?.[0]
      if (file) {
        handleFileSelect(endpoint, file)
      }
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback(
    (endpoint: EndpointKey, e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setDragOver(endpoint)
    },
    []
  )

  const handleDragLeave = useCallback(() => {
    setDragOver(null)
  }, [])

  const removeSelected = useCallback((endpoint: EndpointKey) => {
    setSelectedFiles((prev) => {
      const updated = { ...prev }
      delete updated[endpoint]
      return updated
    })
  }, [])

  const removeUploaded = useCallback((endpoint: EndpointKey) => {
    setUploadedFiles((prev) => {
      const updated = { ...prev }
      delete updated[endpoint]
      return updated
    })
    setSelectedFiles((prev) => {
      const updated = { ...prev }
      delete updated[endpoint]
      return updated
    })
  }, [])

  const handleUpload = useCallback(
    async (endpoint: EndpointKey) => {
      const file = selectedFiles[endpoint]
      if (!file) return

      setEndPoint(endpoint)
      setIsLoadingUploadFile(true)

      try {
        const base64 = await new Promise<string | null>((resolve) => {
          const reader = new FileReader()
          reader.onabort = () => {
            console.log("file reading was aborted")
            resolve(null)
          }
          reader.onerror = () => {
            console.log("file reading has failed")
            resolve(null)
          }
          reader.onloadend = () => {
            const result = reader.result
            if (!result || typeof result !== "string") return resolve(null)
            resolve(result.replace("data:text/csv;base64,", ""))
          }
          reader.readAsDataURL(file)
        })

        if (!base64) return

        const responseBody = await request<
          UploadSeaMarginRequest,
          UploadSeaMarginResponse
        >({
          url: `${BASE_URL}/prevetting/seamargin`,
          method: "post",
          data: {
            file: base64,
            endPoint: endpoint,
            originalFileName: file.name,
          },
        })

        if (!responseBody) return

        setFileName(file.name)
        if (responseBody.status === 200) {
          toast.success(`Successfully uploaded ${file.name}`, {
            position: "bottom-right",
            autoClose: 5000,
          })
          setUploadedFiles((prev) => ({ ...prev, [endpoint]: file.name }))
          setSelectedFiles((prev) => {
            const updated = { ...prev }
            delete updated[endpoint]
            return updated
          })
        }
      } catch (err) {
        let message = "Error uploading to server"
        if (axios.isAxiosError(err)) {
          const axiosErroMessage = (err?.response as any)?.data?.error?.message
          message = axiosErroMessage
            ? `${message}: ${axiosErroMessage}`
            : `${message}.`
        } else if (err instanceof Error) {
          message = `${message}: ${err.message}`
        } else {
          message = "."
        }
        toast.warn(message, {
          position: "bottom-right",
          autoClose: 8000,
        })
      } finally {
        setIsLoadingUploadFile(false)
        setEndPoint("")
      }
    },
    [selectedFiles, request]
  )

  return (
    <React.Fragment>
      <Helmet>
        <title>Sea Margin | Market Manager Platform</title>
      </Helmet>
      <Container fluid className="my-3 px-4">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
          <Breadcrumbs
            title="Sea Margin"
            breadcrumbItem="Upload Sea Margin File"
          />
        </div>

        {/* Stats Cards */}
        <div className="row g-3 mt-3 mb-4">
          {uploadCategories.map((category) => {
            const uploaded = uploadedFiles[category.id]
            const selected = selectedFiles[category.id]
            const IconComponent = category.icon
            return (
              <div key={category.id} className="col-12 col-md-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex align-items-center justify-content-between p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className={`bg-${category.color} text-white rounded d-flex align-items-center justify-content-center`}
                        style={{ width: "40px", height: "40px" }}
                      >
                        <IconComponent size={20} />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p className="mb-0 fw-medium text-dark">
                          {category.title}
                        </p>
                        <p className="mb-0 small text-muted text-truncate">
                          {uploaded
                            ? uploaded
                            : selected
                            ? "Ready to upload"
                            : "No file yet"}
                        </p>
                      </div>
                    </div>
                    {uploaded && (
                      <CheckCircle size={20} className="text-success flex-shrink-0" />
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Upload Zones */}
        <div className="row g-4">
          {uploadCategories.map((category) => {
            const uploaded = uploadedFiles[category.id]
            const selected = selectedFiles[category.id]
            const isDragging = dragOver === category.id
            const IconComponent = category.icon

            return (
              <div key={category.id} className="col-12 col-md-4">
                <input
                  ref={inputRefs[category.id]}
                  type="file"
                  accept="text/csv"
                  hidden
                  onChange={(e) => handleInputChange(category.id, e)}
                />

                <div
                  className={`card shadow-sm border-2 border-dashed p-4 text-center position-relative ${
                    isDragging
                      ? "border-gray bg-secondary bg-opacity-10"
                      : uploaded
                      ? "border-success bg-success bg-opacity-10"
                      : selected
                      ? "border-warning bg-warning bg-opacity-10"
                      : "border-gray"
                  }`}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    minHeight: "300px",
                  }}
                  onClick={() => handleClick(category.id)}
                  onDragOver={(e) => handleDragOver(category.id, e)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(category.id, e)}
                >
                  <div className="card-body">
                    <div
                      className={`bg-${category.color} text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center shadow`}
                      style={{ width: "64px", height: "64px" }}
                    >
                      <IconComponent size={32} />
                    </div>

                    <h5 className="fw-bold text-dark mb-2">
                      {category.title}
                    </h5>

                    <p className="text-muted small mb-4">
                      {category.description}
                    </p>

                    {uploaded ? (
                      <div className="card border border-success bg-white p-3 mb-3">
                        <div className="d-flex align-items-start justify-content-between">
                          <div className="text-start flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <CheckCircle size={16} className="text-success flex-shrink-0" />
                              <span className="badge bg-success text-uppercase">
                                Uploaded
                              </span>
                            </div>
                            <p className="mb-1 small fw-medium text-dark text-truncate">
                              {uploaded}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0 ms-2 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation()
                              removeUploaded(category.id)
                            }}
                          >
                            <X size={20} />
                          </button>
                        </div>
                      </div>
                    ) : selected ? (
                      <div className="card border border-warning bg-white p-3 mb-3">
                        <div className="d-flex align-items-start justify-content-between">
                          <div className="text-start flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <div
                                className="bg-warning rounded-circle flex-shrink-0"
                                style={{ width: "8px", height: "8px" }}
                              ></div>
                              <span className="badge bg-warning text-uppercase">
                                Selected
                              </span>
                            </div>
                            <p className="mb-1 small fw-medium text-dark text-truncate">
                              {selected.name}
                            </p>
                            <p className="mb-0 text-muted" style={{ fontSize: "0.75rem" }}>
                              {(selected.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0 ms-2 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation()
                              removeSelected(category.id)
                            }}
                          >
                            <X size={20}/>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <p className="small fw-medium text-muted">
                          Drop file here or click to browse
                        </p>
                      </div>
                    )}

                    <div className="d-inline-flex align-items-center gap-2 px-3 py-2 bg-light rounded">
                      {uploaded ? (
                        <>
                          <Upload size={14} />
                          <span className="small text-muted">Upload new file</span>
                        </>
                      ) : (
                        <>
                          <FileText size={14} />
                          <span className="small text-muted">
                            {selected ? "Change file" : "Choose CSV file"}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Upload Button */}
                {selected && (
                  <button
                    type="button"
                    className={`btn btn-${category.color} w-100 mt-3 py-2 shadow-sm d-flex align-items-center justify-content-center gap-2`}
                    onClick={() => handleUpload(category.id)}
                    disabled={
                      isLoadingUploadFile && endPoint === category.id
                    }
                  >
                    {isLoadingUploadFile && endPoint === category.id ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={18} />
                        <span>Upload {category.title}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {/* Help Text */}
        <div className="text-center mt-5">
          <div className="d-inline-flex align-items-center gap-2 px-4 py-2 bg-white rounded-pill shadow-sm border">
            <div
              className="bg-success rounded-circle"
              style={{
                width: "8px",
                height: "8px",
                animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
              }}
            ></div>
            <p className="mb-0 small text-muted">
              System ready • Select CSV files then click upload button
            </p>
          </div>
        </div>
      </Container>
    </React.Fragment>
  )
}

export default SeaMarginWrapper